import {
  Component,
  Input,
  HostListener,
  ViewChild,
  OnInit,
  HostBinding,
  DoCheck,
  ElementRef,
  AfterViewInit,
  Output,
  EventEmitter,
} from '@angular/core';
import { FileStorageService } from '../../services';
import { NgxGalleryPreviewComponent } from './ngx-gallery-preview.component';
import { NgxGalleryImageComponent } from './ngx-gallery-image.component';
import { NgxGalleryThumbnailsComponent } from './ngx-gallery-thumbnails.component';
import { NgxGalleryHelperService } from './ngx-gallery-helper.service';

import { NgxGalleryOptions } from './ngx-gallery-options.model';
import { INgxGalleryImageInfo, NgxGalleryImage } from './ngx-gallery-image.model';
import { NgxGalleryLayout } from './ngx-gallery-layout.model';
import { NgxGalleryOrderedImage } from './ngx-gallery-ordered-image.model';

@Component({
  selector: 'ngx-gallery',
  templateUrl: './ngx-gallery.component.html',
  styleUrls: ['./ngx-gallery.component.scss'],
  providers: [NgxGalleryHelperService],
})
export class NgxGalleryComponent implements OnInit, DoCheck, AfterViewInit {
  @Input() options: NgxGalleryOptions[];
  @Input() images: NgxGalleryImage[];

  @Output() imagesReady = new EventEmitter();
  @Output() change = new EventEmitter<{ index: number; image: NgxGalleryImage }>();
  @Output() previewOpen = new EventEmitter();
  @Output() previewClose = new EventEmitter();
  @Output() previewChange = new EventEmitter<{ index: number; image: NgxGalleryImage }>();

  smallImages: INgxGalleryImageInfo[];
  mediumImages: NgxGalleryOrderedImage[];
  bigImages: INgxGalleryImageInfo[];
  links: string[];

  oldImages: NgxGalleryImage[];
  oldImagesLength = 0;

  selectedIndex = 0;
  previewEnabled: boolean;

  currentOptions: NgxGalleryOptions;

  private breakpoint: number | undefined = undefined;
  private prevBreakpoint: number | undefined = undefined;
  private fullWidthTimeout: any;

  @ViewChild(NgxGalleryPreviewComponent) preview: NgxGalleryPreviewComponent;
  @ViewChild(NgxGalleryImageComponent) image: NgxGalleryImageComponent;
  @ViewChild(NgxGalleryThumbnailsComponent) thubmnails: NgxGalleryThumbnailsComponent;

  @HostBinding('style.width') width: string;
  @HostBinding('style.height') height: string;
  @HostBinding('style.left') left: string;

  constructor(private myElement: ElementRef, private fileStorageService: FileStorageService) {}

  ngOnInit() {
    this.options = this.options.map((opt) => new NgxGalleryOptions(opt));
    this.sortOptions();
    this.setBreakpoint();
    this.setOptions();
    this.checkFullWidth();
    if (this.currentOptions) {
      this.select(<number>this.currentOptions.startIndex);
    }
  }

  ngDoCheck(): void {
    if (
      (this.images !== undefined && this.images.length !== this.oldImagesLength) ||
      this.images !== this.oldImages
    ) {
      this.oldImagesLength = this.images.length;
      this.oldImages = this.images;
      this.setImages();

      if (this.images && this.images.length) {
        this.imagesReady.emit();
      }

      if (this.image) {
        this.image.reset(<number>this.currentOptions.startIndex);
      }

      if (this.thubmnails) {
        this.thubmnails.reset(<number>this.currentOptions.startIndex);
      }
    }
  }

  ngAfterViewInit(): void {
    this.checkFullWidth();
    if (this.currentOptions.showPreview) {
      this.select(this.currentOptions.startIndex ?? 0);
      this.openPreview(this.selectedIndex);
    }
  }

  @HostListener('window:resize') onResize() {
    this.setBreakpoint();

    if (this.prevBreakpoint !== this.breakpoint) {
      this.setOptions();
    }

    if (this.currentOptions && this.currentOptions.fullWidth) {
      if (this.fullWidthTimeout) {
        clearTimeout(this.fullWidthTimeout);
      }

      this.fullWidthTimeout = setTimeout(() => {
        this.checkFullWidth();
      }, 200);
    }
  }

  getImageHeight(): string {
    return this.currentOptions && this.currentOptions.thumbnails
      ? this.currentOptions.imagePercent + '%'
      : '100%';
  }

  getThumbnailsHeight(): string {
    if (this.currentOptions && this.currentOptions.image) {
      return (
        'calc(' +
        this.currentOptions.thumbnailsPercent +
        '% - ' +
        this.currentOptions.thumbnailsMargin +
        'px)'
      );
    } else {
      return '100%';
    }
  }

  getThumbnailsMarginTop(): string {
    if (this.currentOptions && this.currentOptions.layout === NgxGalleryLayout.ThumbnailsBottom) {
      return this.currentOptions.thumbnailsMargin + 'px';
    } else {
      return '0px';
    }
  }

  getThumbnailsMarginBottom(): string {
    if (this.currentOptions && this.currentOptions.layout === NgxGalleryLayout.ThumbnailsTop) {
      return this.currentOptions.thumbnailsMargin + 'px';
    } else {
      return '0px';
    }
  }

  openPreview(index: number): void {
    this.previewEnabled = true;
    this.preview.open(index);
  }

  onPreviewOpen(): void {
    this.previewOpen.emit();

    if (this.image && this.image.autoPlay) {
      this.image.stopAutoPlay();
    }
  }

  onPreviewClose(): void {
    this.previewEnabled = false;
    this.previewClose.emit();

    if (this.image && this.image.autoPlay) {
      this.image.startAutoPlay();
    }
  }

  selectFromImage(index: number) {
    this.select(index);
  }

  selectFromThumbnails(index: number) {
    this.select(index);

    if (
      this.currentOptions &&
      this.currentOptions.thumbnails &&
      this.currentOptions.preview &&
      !this.currentOptions.thumbnailShowPreviewOnAction &&
      (!this.currentOptions.image || this.currentOptions.thumbnailsRemainingCount)
    ) {
      this.openPreview(this.selectedIndex);
    }
  }

  showPreviewFromThumbnail(index: number) {
    this.select(index);
    this.openPreview(this.selectedIndex);
  }

  download(index: number) {
    this.select(index);

    if (this.images[index]?.origin) {
      const objectKey = (this.images[index] as any).origin.objectKey;
      const keyUri = this.fileStorageService.getDownloadFileUrl(objectKey);
      this.fileStorageService.downloadFile(keyUri, this.images[index].extension);
    } else if (this.images[index]?.key) {
      const keyUri = this.fileStorageService.getDownloadFileUrl(this.images[index].key);
      this.fileStorageService.downloadFile(keyUri, this.images[index].extension);
    } else if (this.bigImages[index]?.url) {
      const uri = this.bigImages[index].url;
      this.downloadViaOpenNewLink(uri);
    }
  }

  private downloadViaOpenNewLink(url) {
    const link = document.createElement('a');
    link.href = url;
    link.download = 'image.png';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  show(index: number): void {
    this.select(index);
  }

  showNext(): void {
    this.image.showNext();
  }

  showPrev(): void {
    this.image.showPrev();
  }

  canShowNext(): boolean {
    if (this.images && this.currentOptions) {
      return this.currentOptions.imageInfinityMove || this.selectedIndex < this.images.length - 1
        ? true
        : false;
    } else {
      return false;
    }
  }

  canShowPrev(): boolean {
    if (this.images && this.currentOptions) {
      return this.currentOptions.imageInfinityMove || this.selectedIndex > 0 ? true : false;
    } else {
      return false;
    }
  }

  previewSelect(index: number) {
    this.previewChange.emit({ index, image: this.images[index] });
  }

  private select(index: number) {
    this.selectedIndex = index;

    this.change.emit({
      index,
      image: this.images[index],
    });
  }

  private checkFullWidth(): void {
    if (this.currentOptions && this.currentOptions.fullWidth) {
      this.width = document.body.clientWidth + 'px';
      this.left =
        -(document.body.clientWidth - this.myElement.nativeElement.parentNode.innerWidth) / 2 +
        'px';
    }
  }

  private setImages(): void {
    this.smallImages = this.images.map((img) => ({
      extension: img.extension,
      url: img.small,
      leftIcon: img.leftIcon,
    }));
    this.mediumImages = this.images.map(
      (img, i) =>
        new NgxGalleryOrderedImage({
          src: img.medium,
          index: i,
        }),
    );
    this.bigImages = this.images.map((img) => ({
      extension: img.extension,
      url: img.big,
      leftIcon: img.leftIcon,
      descriptionData: img.descriptionData,
    }));
    this.links = this.images.map((img) => <string>img.url);
  }

  private setBreakpoint(): void {
    this.prevBreakpoint = this.breakpoint;
    let breakpoints;

    if (typeof window !== 'undefined') {
      breakpoints = this.options
        .filter((opt) => opt.breakpoint >= window.innerWidth)
        .map((opt) => opt.breakpoint);
    }

    if (breakpoints && breakpoints.length) {
      this.breakpoint = breakpoints.pop();
    } else {
      this.breakpoint = undefined;
    }
  }

  private sortOptions(): void {
    this.options = [
      ...this.options.filter((a) => a.breakpoint === undefined),
      ...this.options
        .filter((a) => a.breakpoint !== undefined)
        .sort((a, b) => b.breakpoint - a.breakpoint),
    ];
  }

  private setOptions(): void {
    this.currentOptions = new NgxGalleryOptions({});

    this.options
      .filter((opt) => opt.breakpoint === undefined || opt.breakpoint >= this.breakpoint)
      .map((opt) => this.combineOptions(this.currentOptions, opt));

    this.width = <string>this.currentOptions.width;
    this.height = <string>this.currentOptions.height;
  }

  private combineOptions(first: NgxGalleryOptions, second: NgxGalleryOptions) {
    Object.keys(second).map(
      (val) => (first[val] = second[val] !== undefined ? second[val] : first[val]),
    );
  }
}
